import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { checkFormEqualWhenDirty, checkFormListValidateWhenDirty, isStringEmpty } from '@common/helpers';
import { ArrivalDepartureNoticeInformationState } from '@common/helpers/constants/my-booking/passenger-information-input-modal.state';
import { MListData } from '@common/interfaces';
import {
  ArrivalDepartureNoticeInformationData,
  ArrivalDepartureNoticeInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/arrival-departure-notice-information.interface';
import { PassengerInformationInputModalStoreService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal-store.service';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { AswValidators } from '@lib/helpers';
import { StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { GetOrderResponseData, GetOrderResponseDataAir } from 'src/sdk-servicing';
import { Subscription } from 'rxjs';

@Component({
  selector: 'asw-arrival-departure-notice-information',
  templateUrl: './arrival-departure-notice-information.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArrivalDepartureNoticeInformationComponent implements AfterViewInit, OnDestroy {
  @Input()
  get data(): ArrivalDepartureNoticeInformationData | undefined {
    return this._data;
  }
  set data(value: ArrivalDepartureNoticeInformationData | undefined) {
    this._data = value;
    this.onInit();
  }
  private _data?: ArrivalDepartureNoticeInformationData;

  @Output()
  dataChange: EventEmitter<ArrivalDepartureNoticeInformationData> =
    new EventEmitter<ArrivalDepartureNoticeInformationData>();

  @Input()
  get parts(): ArrivalDepartureNoticeInformationParts | undefined {
    return this._parts;
  }
  set parts(value: ArrivalDepartureNoticeInformationParts | undefined) {
    this._parts = value;
    this.onInit();
  }
  private _parts?: ArrivalDepartureNoticeInformationParts;

  @Input()
  set isOpen(value: boolean) {
    this._isOpen = value;
    this.isOpenChange.emit(value);
    this._change.markForCheck();
  }
  get isOpen() {
    return this._isOpen;
  }
  private _isOpen: boolean = false;

  @Output()
  isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public ARRIVAL_DEPARTURE_NOTICE_FORMS = {
    MAIL_ADDRESS: 'mailAddress',
    CONFIRM_MAIL_ADDRESS: 'confirmMailAddress',
    MAIL_RECIPIENT_NAME: 'mailRecipientName',
    MAIL_LANG: 'mailLang',
    SECOND_MAIL_ADDRESS: 'secondMailAddress',
    SECOND_CONFIRM_MAIL_ADDRESS: 'secondConfirmMailAddress',
    SECOND_MAIL_RECIPIENT_NAME: 'secondMailRecipientName',
    SECOND_MAIL_LANG: 'secondMailLang',
  };

  public formGroup = new FormGroup<{ [key: string]: FormControl }>({
    // (50.1.1.7.2) 発着通知メールアドレス
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS]: new FormControl('', [
      AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
    ]),
    // (50.1.1.7.3) 発着通知メールアドレス(確認用)
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS]: new FormControl('', [
      AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
    ]),
    // (50.1.1.7.1) 発着通知メール受信者名
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME]: new FormControl('', [
      AswValidators.alphaSpace({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
    ]),
    // (50.1.1.7.2) 発着通知メールアドレス
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS]: new FormControl('', [
      AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
    ]),
    // (50.1.1.7.3) 発着通知メールアドレス(確認用)
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS]: new FormControl('', [
      AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
    ]),
    // (50.1.1.7.1) 発着通知メール受信者名
    [this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME]: new FormControl('', [
      AswValidators.alphaSpace({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
    ]),
  });

  private formGroupvalueChangesSubscription = new Subscription();

  get showIsOpenClass() {
    return this.isOpen || (this.data && this.data.emails.length >= 1);
  }

  constructor(
    private _change: ChangeDetectorRef,
    private _staticMsg: StaticMsgPipe,
    private _s04CommonSvc: PassengerInformationInputModalCommonService,
    private _common: CommonLibService,
    private _s04StoreSvc: PassengerInformationInputModalStoreService
  ) {}

  ngAfterViewInit(): void {
    Object.values(this.formGroup.controls).forEach((control) => control.setErrors(null));

    this.formGroupvalueChangesSubscription.add(
      this.formGroup.valueChanges.subscribe(() => {
        Object.values(this.formGroup.controls).forEach((control) => control.markAsDirty());
      })
    );
  }

  ngOnDestroy(): void {
    if (this.formGroupvalueChangesSubscription) {
      this.formGroupvalueChangesSubscription.unsubscribe();
    }
  }

  onInit() {
    if (!this.data || !this.parts) {
      return;
    }

    if (this.data.emails.length < 3) {
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].setValue(
        this.data.emails?.[0]?.recipient ?? ''
      );
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].setValue(
        this.data.emails?.[0]?.address ?? ''
      );
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].setValue(
        this.data.emails?.[0]?.confirmMailAddress ?? ''
      );

      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].setValue(
        this.data.emails?.[1]?.recipient ?? ''
      );
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].setValue(
        this.data.emails?.[1]?.address ?? ''
      );
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].setValue(
        this.data.emails?.[1]?.confirmMailAddress ?? ''
      );

      this.isOpen = this.isOpen || this.parts.isDisplay;
    }

    this._change.markForCheck();
  }

  /**
   * (30) 発着通知連絡先情報追加ボタン押下処理　※発着通知連絡先情報パーツ
   */
  onClickShutter() {
    this.isOpen = !this.isOpen;
    this._change.markForCheck();
    this.isOpenChange.emit(this.isOpen);
  }

  update(requiredCheckError: boolean = false) {
    this.checkArrivalDepartureNoticeInformationData();

    if (!this.data) {
      return;
    }

    this.data.emails[0].recipient =
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].value ?? '';
    this.data.emails[0].address = this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].value ?? '';
    this.data.emails[0].confirmMailAddress =
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].value ?? '';

    this.data.emails[1].recipient =
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].value ?? '';
    this.data.emails[1].address =
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].value ?? '';
    this.data.emails[1].confirmMailAddress =
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].value ?? '';

    if (
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].value ||
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].value ||
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].value
    ) {
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
        AswValidators.alphaSpace({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
      ]);
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
        AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
      ]);
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
        AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
      ]);
    } else {
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].clearValidators();
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].clearValidators();
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].clearValidators();
    }
    if (
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].value ||
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].value ||
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].value
    ) {
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
        AswValidators.alphaSpace({ params: ArrivalDepartureNoticeInformationState.Params.Recipient }),
      ]);
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
        AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.Email }),
      ]);
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].setValidators([
        AswValidators.required({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
        AswValidators.email({ params: ArrivalDepartureNoticeInformationState.Params.ConfirmEmail }),
      ]);
    } else {
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].clearValidators();
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].clearValidators();
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].clearValidators();
    }

    const checkList = [
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME],
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS],
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS],
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME],
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS],
      this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS],
    ];

    this.data.isError = checkFormListValidateWhenDirty(checkList, requiredCheckError);

    if (
      (!isStringEmpty(this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].value) ||
        !isStringEmpty(this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].value) ||
        requiredCheckError) &&
      checkFormEqualWhenDirty(
        this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS],
        this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS],
        ArrivalDepartureNoticeInformationState.Errors.ConfirmEmail
      )
    ) {
      this.data.isError = true;
    }

    if (
      (!isStringEmpty(this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].value) ||
        !isStringEmpty(
          this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].value
        ) ||
        requiredCheckError) &&
      checkFormEqualWhenDirty(
        this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS],
        this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS],
        ArrivalDepartureNoticeInformationState.Errors.ConfirmEmail
      )
    ) {
      this.data.isError = true;
    }

    this._change.markForCheck();
    this.dataChange.emit(this.data);
  }

  updateFormValueAndValidity() {
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_RECIPIENT_NAME].updateValueAndValidity();
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.MAIL_ADDRESS].updateValueAndValidity();
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.CONFIRM_MAIL_ADDRESS].updateValueAndValidity();
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_RECIPIENT_NAME].updateValueAndValidity();
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_MAIL_ADDRESS].updateValueAndValidity();
    this.formGroup.controls[this.ARRIVAL_DEPARTURE_NOTICE_FORMS.SECOND_CONFIRM_MAIL_ADDRESS].updateValueAndValidity();
  }

  checkArrivalDepartureNoticeInformationData() {
    this.data ??= { emails: [], isError: false };

    if (!this.data.emails[0]) {
      this.data.emails.push({
        recipient: '',
        address: '',
        confirmMailAddress: '',
        lang: '',
      });
    }

    if (!this.data.emails[1]) {
      this.data.emails.push({
        recipient: '',
        address: '',
        confirmMailAddress: '',
        lang: '',
      });
    }
  }

  getLabelByIndex(index: number) {
    return `${this._staticMsg.transform('label.receiversAddress')} ${index + 1}`;
  }

  get pnr(): GetOrderResponseData | undefined {
    return this._s04StoreSvc.pnr();
  }

  isHideLanguage() {
    return (
      Boolean(this._common.isJapaneseOffice()) &&
      Boolean(this.pnr?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic)
    );
  }
}
